import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { AutogiroMandateStatusDescription, AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { AutogiroActionEnum } from 'src/shared/models/enums/autogiroAction.enum';
import {
  cancelAutogiroMandate,
  deleteAutogiroMandate,
  getAutogiroMandateById,
  updateAutogiroMandate,
} from 'src/shared/services/autogiroMandateService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AutogiroMandateSourceEnum } from 'src/shared/models/enums/autogiroMandateSource.enum';
import { AutogiroMandateSentEnum } from 'src/shared/models/enums/autogiroMandateSent.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import { useTranslation } from 'react-i18next';
import { DeleteOutline, Edit, InfoOutlined } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { AutogiroMandateLog } from './autogiroMandateLog';
import { AutogiroMandateAlertMessage } from './autogiroMandateAlertMessage';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';
import { AutogiroMandatePutRequest } from '../models/autogiroMandatePutRequest';

interface FormValues {
  clearingNr: string;
  accountNr: string;
  status: number;
  sent: AutogiroMandateSentEnum;
  personalIdentityNumber: string;
}

type AutogiroMandateModalProps = {
  modalOpen: boolean;
  autogiroMandateId: number;
  onSave: () => void;
  onClose: () => void;
};

export const AutogiroMandateModal = (props: AutogiroMandateModalProps) => {
  const { modalOpen, autogiroMandateId, onSave, onClose } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const theme = useTheme();

  const [cancelStatus, setCancelStatus] = useState<AutogiroActionEnum>(AutogiroActionEnum.NoAction);
  const [loading, setLoading] = useState<boolean>(false);
  const [autogiroMandate, setAutogiroMandate] = useState<AutogiroMandateResponse>();
  const [editMandate, setEditMandate] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const { currentUser } = useGlobals();

  const handleDeleteMandate = useCallback(async () => {
    if (!autogiroMandate) return;

    setLoading(true);

    try {
      await deleteAutogiroMandate(autogiroMandate.autogiroMandate_id);

      if (isMounted()) {
        toast.success(<>{t('invoice:autogiro.deleteMandateSuccess')}</>);
        onSave();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:autogiro.deleteMandateError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [autogiroMandate, isMounted, onSave, t]);

  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const status = autogiroMandate?.status;
  const canSave = editMandate || cancelStatus === AutogiroActionEnum.Cancel;
  const editable =
    status === AutogiroMandateStatusEnum.NotApproved ||
    status === AutogiroMandateStatusEnum.Error ||
    status === AutogiroMandateStatusEnum.New ||
    status === AutogiroMandateStatusEnum.PendingApproval ||
    status === AutogiroMandateStatusEnum.PendingCancel;
  const gridSizeEditable = status === AutogiroMandateStatusEnum.NotApproved || status === AutogiroMandateStatusEnum.Error ? 10 : 12;
  // Ett medgivande som ligger registrerat hos Bankgirot ska makuleras och inte raderas. Kvar att
  // radera är det som inte finns där: aldrig skickat, avslaget av Bankgirot, eller fel på vägen.
  //
  // Statusen räcker som villkor. Ett avslag kommer alltid efter att filen skickats, så ett krav på
  // att medgivandet aldrig skickats gjorde Ej godkänd omöjlig att radera.
  const canDelete =
    (status === AutogiroMandateStatusEnum.New || status === AutogiroMandateStatusEnum.NotApproved || status === AutogiroMandateStatusEnum.Error) &&
    getPermission(currentUser, PermissionEnum.AutogiroDelete);
  const linkedName = autogiroMandate?.linkedMemberName || autogiroMandate?.linkedDonorName || '';

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const mandateSchema: Schema<FormValues> = yup.object().shape({
    clearingNr: yup
      .string()
      .matches(RegexHelper.clearingNr, t('invoice:createAutogiroMandate.clearingNrFormat'))
      .required(t('common:validationMessages.required')),
    accountNr: yup.string().required(t('common:validationMessages.required')),
    status: yup.number().required(t('common:validationMessages.required')),
    sent: yup.number().required(),
    personalIdentityNumber: yup.string().required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(mandateSchema),
    mode: 'all',
    defaultValues: {
      accountNr: autogiroMandate?.accountNr || '',
      clearingNr: autogiroMandate?.clearingNr || '',
      status: autogiroMandate?.status || AutogiroMandateStatusEnum.Unknown,
      sent: autogiroMandate?.sent || AutogiroMandateSentEnum.NotSent,
      personalIdentityNumber: autogiroMandate?.personalIdentityNumber || '',
    },
  });

  const { handleSubmit, watch, setValue } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAutogiroMandateById(autogiroMandateId);
      if (isMounted()) {
        setAutogiroMandate(res.data);
        setValue('accountNr', res.data.accountNr);
        setValue('clearingNr', res.data.clearingNr);
        setValue('status', res.data.status);
        setValue('sent', res.data.sent);
        setValue('personalIdentityNumber', res.data.personalIdentityNumber);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [autogiroMandateId, isMounted, setValue]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleAutogiroEditActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +(event.target as HTMLInputElement).value as AutogiroActionEnum;
    setCancelStatus(newValue);
  };

  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (!autogiroMandate) {
        toast.error(<>{t('invoice:autogiro.error')}</>);
      } else {
        setLoading(true);
        const dataToPost: AutogiroMandatePutRequest = {
          accountNr: data.accountNr,
          clearingNr: data.clearingNr,
          status: data.status,
          sent: data.sent,
          personalIdentityNumber: data.personalIdentityNumber,

          autogiroMandate_id: autogiroMandate.autogiroMandate_id,
          owner_id: autogiroMandate.owner_id,
          firstName: autogiroMandate.firstName,
          lastName: autogiroMandate.lastName,
          bank: autogiroMandate.bank,
          payerNr: autogiroMandate.payerNr,
          source: autogiroMandate.source,
        };

        try {
          await updateAutogiroMandate(dataToPost);
          if (isMounted()) {
            toast.success(<>{t('invoice:autogiro.update')}</>);
            onSave();
          }
        } catch (e) {
          if (isMounted()) ConsoleHelper.log(e);
        } finally {
          if (isMounted()) setLoading(false);
        }
      }
    },
    [autogiroMandate, isMounted, onSave, t],
  );

  const handleCancelAutogiroMandate = async () => {
    if (autogiroMandate?.autogiroMandate_id && cancelStatus !== AutogiroActionEnum.NoAction) {
      try {
        const res = await cancelAutogiroMandate(autogiroMandate.autogiroMandate_id);
        if (isMounted()) {
          ConsoleHelper.log('RES CancelMandate', res);
          toast.success(<>{t('invoice:autogiro.cancelMandateSuccess')}</>);
          onSave();
        }
      } catch (error) {
        if (isMounted()) {
          ConsoleHelper.log(error);
        }
      }
    }
  };

  const GridItem = ({ label, value }) => {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
          {label}:
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
          {value || '—'}
        </Typography>
      </Grid>
    );
  };

  const statusOptions = [
    {
      value: AutogiroMandateStatusEnum.New,
      label: t('invoice:autogiroMandateStatus.New'),
    },
    ...(autogiroMandate?.sent === AutogiroMandateSentEnum.Sent
      ? [
          {
            value: AutogiroMandateStatusEnum.Approved,
            label: t('invoice:autogiroMandateStatus.Approved'),
          },
        ]
      : []),
  ];

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog
          onClose={onHandleClose}
          aria-labelledby={`${componentId}-titel`}
          open={modalOpen}
          maxWidth='md'
          fullWidth
          sx={{
            minWidth: {
              xs: '90vw',
              md: '400px',
            },
            '& .MuiDialogContent-root': {
              p: 2,
            },
            '& .MuiDialogActions-root': {
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2, lineHeight: 1.2 }} id={`${componentId}-titel`}>
            {t('invoice:autogiro.tabAutogiro')}
            <DialogCloseButton handleClose={onHandleClose} />
          </DialogTitle>
          <LoadingOverlay loading={loading} />
          {autogiroMandate && (
            <>
              <AutogiroMandateAlertMessage item={autogiroMandate} />
              <DialogContent>
                {editable && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: { xs: 0, md: 2 }, mb: -5 }}>
                    <Button
                      variant='outlined'
                      color='primary'
                      size='small'
                      disableElevation
                      onClick={() => setEditMandate(true)}
                      endIcon={<Edit fontSize='small' />}
                    >
                      {t('common:button.edit')}
                    </Button>
                  </Box>
                )}
                <Grid container>
                  <Grid item xs={autogiroMandate.status === AutogiroMandateStatusEnum.Approved ? 8.5 : gridSizeEditable}>
                    <Grid container spacing={2}>
                      <>
                        <GridItem label={t('invoice:autogiro:labels.name')} value={`${autogiroMandate.firstName} ${autogiroMandate.lastName}`} />

                        {!editMandate ? (
                          <GridItem label={t('invoice:autogiro:labels.personalIdentityNumber')} value={autogiroMandate.personalIdentityNumber} />
                        ) : (
                          <Grid item xs={12} sm={6} md={4}>
                            <Grid item xs={10}>
                              <ControlledTextField name='personalIdentityNumber' label={t('invoice:autogiro:labels.personalIdentityNumber')} />
                            </Grid>
                          </Grid>
                        )}

                        <GridItem
                          label={t('invoice:autogiro:labels.source')}
                          value={t(`invoice:autogiroMandateSource.${AutogiroMandateSourceEnum[autogiroMandate.source ?? 0]}`)}
                        />

                        <GridItem label={t('invoice:autogiro:labels.payerNr')} value={autogiroMandate.payerNr} />

                        {!editMandate ? (
                          <GridItem label={t('invoice:autogiro:labels.clearingNr')} value={autogiroMandate.clearingNr} />
                        ) : (
                          <Grid item xs={12} sm={6} md={4}>
                            <Grid item xs={10}>
                              <Tooltip
                                title={
                                  <span>
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrTooltip')}
                                    <br />
                                    <br />
                                    <strong>{t('invoice:createAutogiroMandateTooltip.swedbank')}</strong>
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrSwedbank')}
                                    <br />
                                    <br />
                                    <strong>{t('invoice:createAutogiroMandateTooltip.handelsbankens')}</strong>
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrHandelsbankens')}
                                    <br />
                                    <br />
                                    <strong>{t('invoice:createAutogiroMandateTooltip.personAccNordea')}</strong>
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrPersonAccNordea')}
                                    <br />
                                    <br />
                                    <strong>{t('invoice:createAutogiroMandateTooltip.personAccNordea')}</strong>
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrPersonAccNordeaNonPP')}
                                    <br />
                                    <br />
                                    {t('invoice:createAutogiroMandateTooltip.clearingNrPlusGiro')}
                                  </span>
                                }
                                arrow
                                placement='right'
                                aria-hidden
                              >
                                <span>
                                  <ControlledTextField name='clearingNr' label={t('invoice:autogiro:labels.clearingNr')} />
                                </span>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        )}

                        {!editMandate ? (
                          <GridItem label={t('invoice:autogiro:labels.accountNr')} value={autogiroMandate.accountNr} />
                        ) : (
                          <Grid item xs={12} sm={6} md={4}>
                            <Grid item xs={10}>
                              <ControlledTextField name='accountNr' label={t('invoice:autogiro:labels.accountNr')} />
                            </Grid>
                          </Grid>
                        )}

                        {!editMandate ? (
                          <GridItem
                            label={t('invoice:autogiro:labels.status')}
                            value={t(`invoice:autogiroMandateStatus.${AutogiroMandateStatusEnum[autogiroMandate.status ?? 0]}`)}
                          />
                        ) : (
                          <Grid item xs={12} sm={6} md={4}>
                            <Box display='flex' alignItems='center' gap={1}>
                              <Box>
                                <ControlledAutocompleteDropdown
                                  componentWidth='11rem'
                                  placeholder={
                                    watch('status') !== null ? AutogiroMandateStatusDescription(watch('status'), t) : t('common:button.choose')
                                  }
                                  nameProp='label'
                                  name='status'
                                  label={t('invoice:autogiro:labels.status')}
                                  options={statusOptions}
                                  callback={(selectedOption) => {
                                    setValue('sent', autogiroMandate.sent);
                                    if (selectedOption) {
                                      ConsoleHelper.log(selectedOption);
                                      if (selectedOption.value === AutogiroMandateStatusEnum.New) {
                                        setValue('sent', AutogiroMandateSentEnum.NotSent);
                                      }
                                      setValue('status', selectedOption.value);
                                    }
                                  }}
                                />
                              </Box>
                              <Tooltip
                                title={<span style={{ whiteSpace: 'pre-line' }}>{t('invoice:autogiro.statusInfo')}</span>}
                                arrow
                                placement='right'
                              >
                                <IconButton size='small'>
                                  <InfoOutlined color='info' fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        )}

                        <GridItem label={t('invoice:autogiro:labels.created')} value={DateHelper.getDateTime(autogiroMandate.createdAt)} />
                        <GridItem
                          label={t('invoice:autogiro:labels.sent')}
                          value={autogiroMandate.sent === AutogiroMandateSentEnum.Sent ? t('common:button.yes') : t('common:button.no')}
                        />
                        <GridItem
                          label={t('invoice:autogiro:labels.isSigned')}
                          value={autogiroMandate.isSigned ? t('common:button.yes') : t('common:button.no')}
                        />
                      </>
                    </Grid>
                  </Grid>

                  {autogiroMandate.status === AutogiroMandateStatusEnum.Approved && (
                    <>
                      {isXs && (
                        <Grid item xs={12} my={2}>
                          <Divider orientation='horizontal' />
                        </Grid>
                      )}
                      <Divider orientation='vertical' flexItem={!isXs} />
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{
                          pl: { xs: 0, sm: 3 },
                          ml: { xs: 0, sm: 2 },
                        }}
                      >
                        <Typography variant='h6'>{t('invoice:incomingBankPayment.transactionActionHeader')}</Typography>
                        <FormControl>
                          <RadioGroup value={cancelStatus} onChange={handleAutogiroEditActionChange}>
                            <FormControlLabel
                              value={AutogiroActionEnum.NoAction}
                              control={<Radio />}
                              label={t('invoice:incomingBankPayment.transactionActionEnum.NoAction')}
                            />

                            <FormControlLabel value={AutogiroActionEnum.Cancel} control={<Radio />} label={t('invoice:autogiro.cancel')} />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DialogContent>
              <Grid mt={2} item xs={12}>
                <AutogiroMandateLog log={autogiroMandate.autogiroMandateLog} />
              </Grid>
            </>
          )}
          <Divider />
          <DialogActions>
            {canDelete && (
              <Box sx={{ p: 1, mr: 'auto' }}>
                <Button variant='outlined' color='error' startIcon={<DeleteOutline />} onClick={() => setOpenDeleteDialog(true)}>
                  {t('common:button.delete')}
                </Button>
              </Box>
            )}
            <Box sx={{ p: 1 }}>
              <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 2 }}>
                {t('common:button.cancel')}
              </Button>
              <LoadingButton
                disabled={loading || !canSave}
                autoFocus
                variant='contained'
                onClick={editable ? handleSubmit(onSubmit) : () => handleCancelAutogiroMandate()}
              >
                {t('common:button.save')}
              </LoadingButton>
            </Box>
          </DialogActions>
          <DeleteConfirmationDialog
            title={t('common:confirmDelete')}
            value=''
            modaltext={
              linkedName ? t('invoice:autogiro.deleteMandateLinkedConfirm', { name: linkedName }) : t('invoice:autogiro.deleteMandateConfirm')
            }
            confirmButtonColor='error'
            open={openDeleteDialog}
            onClose={(value) => {
              setOpenDeleteDialog(false);

              if (value) handleDeleteMandate();
            }}
          />
        </Dialog>
      </form>
    </FormProvider>
  );
};
